import { MAX_MEDIA_SIZE } from "constants/form"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import { receiveErrors } from "modules/errors/reducer"
import { errorFieldsSelector } from "modules/errors/selectors"
import { serialize } from "object-to-formdata"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { updateTrip, getTrip } from "store/trips"
import { CustomInputError } from "ui/CustomInputError"
import { MediaGrid } from "ui/MediaGrid/SortableMediaGrid"
import { MediaUpload } from "ui/MediaUpload"
import { DragAndDrop } from "ui/PersonalInfo/ProfileImages/DragAndDrop"
import { VideoPlayerModal } from "ui/VideoPlayerModal"
import { isMediaFileVideo } from "utils/media-upload"
import style from "./ListingMedia.module.css"
import { Spin, message } from "antd"
import { setFormTouched } from "store/proNewListingForms"
import MediaEditorModal from "ui/MediaEditorModal"

const MAX_COUNT = 10

export const ListingMedia = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id } = useParams()
  const trip = useSelector(modelSelector("trip"))
  // Only initialize localMedia from trip when trip is loaded
  const [localMedia, setLocalMedia] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const filteredMedia = localMedia.filter((media) => !media._destroy)
  const activeMediaLength = filteredMedia.length
  const hasMedia = activeMediaLength > 0
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [mediaToCrop, setMediaToCrop] = useState(null)
  const errors = useSelector(errorFieldsSelector())

  // Loading guard: if trip is not loaded or loading, show spinner
  const isTripLoading = !trip || trip.loading

  useEffect(() => {
    if (!trip) return
    // Always update localMedia from trip when trip changes
    const allMedia = [...(trip?.photos || []), ...(trip?.cropped_photos || []), ...(trip?.trimmed_videos || []), ...(trip?.videos || [])]
    const sortedMedia = allMedia?.toSorted((a, b) => {
      const positionA = a.position ?? 0
      const positionB = b.position ?? 0
      return positionA - positionB
    })
    setLocalMedia(sortedMedia)
  }, [trip])

  useEffect(() => {
    if (!trip) return

  }, [trip])

  const onFileListChange = (media) => {
    setLocalMedia((prev) => [...prev, ...media])
    dispatch(
      receiveErrors({
        data: { message: { listing_media: [] } },
        showToast: false
      })
    )
  }

  const handleVideoConfirm = async ({ media, blob, url, posterUrl, posterBlob }) => {
    if (!media) return
    try {
      setIsLoading(true)
      // Update localMedia entry by id. For videos, use poster image for previews.
      // Create a File from the trimmed video blob so it can be sent to the API
      const trimmedFileName = media?.name ? media.name.replace(/\.[^/.]+$/, "") + "-trimmed.mp4" : `trimmed-${Date.now()}.mp4`
      const trimmedFile = blob ? new File([blob], trimmedFileName, { type: "video/mp4" }) : undefined
      setLocalMedia((prev) =>
        prev.map((item) =>
          item.id === media.id
            ? {
                ...item,
                file_type: "trimmed_video",
                // Attach the trimmed video file so the backend receives the new media
                file: trimmedFile ?? item.file,
                // For server 'preview', store the poster image blob
                blob: posterBlob ?? item.blob,
                // Trimmed video URL for playback (object URL for UX)
                url: url ?? item.url,
                // Thumbnail to show in grid (poster)
                preview_url: posterUrl ?? item.preview_url,
                thumbnail_url: posterUrl ?? item.thumbnail_url,
                file_preview: undefined
              }
            : item
        )
      )
      message.success(t("listings.tabs.media.video_updated_successfully"))
    } catch (e) {
      console.error("Video update error", e)
      message.error(t("listings.tabs.media.video_trim_failed"))
    } finally {
      setIsLoading(false)
      // Modal closes itself in MediaEditorModal on confirm
      setMediaToCrop(null)
    }
  }

  const handleDelete = (media) => {
    if (!media.uid && media.url) {
      const updatedMedia = localMedia.map((item) => (item.id === media.id ? { ...item, _destroy: true } : item))
      const deletedMediaIndex = updatedMedia.findIndex((item) => item.id === media.id)
      const updatedMediaWithPositions = updatedMedia.map((item, index) => {
        if (index > deletedMediaIndex && !item._destroy) {
          return { ...item, position: item.position - 1 }
        }
        return item
      })
      setLocalMedia(updatedMediaWithPositions)
    } else {
      const mediaPosition = localMedia.find((item) => item.id === media.id)?.position
      const filteredMedia = localMedia.filter((item) => item.id !== media.id)
      const updatedPositions = filteredMedia.map((item) => {
        if (item.position > mediaPosition) {
          return { ...item, position: item.position - 1 }
        }
        return item
      })
      setLocalMedia(updatedPositions)
    }
  }

  const handleReorder = (newItems) => {
    setLocalMedia(
      newItems.map(({ id }, index) => ({
        ...localMedia.find((item) => item.id === id),
        position: index
      }))
    )
  }

  const handleMediaClick = (media) => {
    if (isMediaFileVideo(media)) {
      setSelectedMedia(media)
      setVideoModalOpen(true)
    }
  }

  const handleCrop = (media) => {
    // Open unified editor for both images and videos
    setMediaToCrop(media)
    setCropModalOpen(true)
  }

  const handleCropComplete = async (croppedBlob) => {
    if (!croppedBlob || !mediaToCrop) return

    try {
      setIsLoading(true)

      // Create a new file with a unique name
      const fileName = mediaToCrop.name || `cropped-${Date.now()}.jpg`
      const croppedFile = new File([croppedBlob], fileName, { type: "image/jpeg" })

      // Create a new URL for the cropped image
      const croppedUrl = URL.createObjectURL(croppedFile)

      setLocalMedia((prevMedia) =>
        prevMedia.map((item) =>
          item.id === mediaToCrop.id
            ? {
                ...item,
                file_type: "cropped_photo",
                file: croppedFile,
                url: croppedUrl,
                thumbnail_url: croppedUrl,
                // Clear any existing preview URLs
                preview_url: undefined,
                file_preview: undefined
              }
            : item
        )
      )

      message.success(t("listings.tabs.media.image_updated_successfully"))
    } catch (error) {
      console.error("Error cropping image:", error)
      message.error(t("listings.tabs.media.crop_area_invalid"))
    } finally {
      setIsLoading(false)
      setCropModalOpen(false)
      setMediaToCrop(null)
    }
  }

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false)
    setSelectedMedia(null)
  }

  const onFinish = async (callback) => {
    if (activeMediaLength >= 2) {
      setIsLoading(true)
      const tripAttributes = {
        media_attributes: localMedia.map(({ id, file_type, blob, _destroy, position, file }) => {
          return {
            file_type,
            _destroy,
            position,
            ...(file ? { file } : { id }),
            ...(file_type === "video" || file_type === "trimmed_video" ? { preview: blob } : {})
          }
        })
      }
      const serializedFormData = serialize({ trip: tripAttributes }, { indices: true })
      dispatch(updateTrip(id, serializedFormData))
        .then(() => {
          dispatch(setFormTouched("media", true))
          setIsLoading(false)
          callback?.()
        })
        .catch(() => {
          setIsLoading(false)
        })
    } else {
      dispatch(
        receiveErrors({
          data: { message: { listing_media: [t("listings.tabs.media.min_photos_error")] } },
          showToast: false
        })
      )
    }
  }



  useEffect(() => {
    if (!trip?.loaded) {
      dispatch(getTrip(id))
    }
  }, [trip, id, dispatch])

  if (isTripLoading) {
    return <Spin spinning={true} size="large" style={{ width: "100%" }} />
  }

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <div className={`flex-column ${style.mediaWrap}`}>
        <div className="position-relative">
          <MediaUpload
            className="mb-m block"
            imageInfo={t("media_upload.image_size_limit", { maxSize: MAX_MEDIA_SIZE.image })}
            videoInfo={t("media_upload.video_size_limit", { maxSize: MAX_MEDIA_SIZE.video })}
            onFileListChange={onFileListChange}
            lastPosition={activeMediaLength}
            maxCount={MAX_COUNT}
            maxCountErrorMessage={`${t("listings.tabs.media.max_photos_error")} ${MAX_COUNT}`}
            initialCount={activeMediaLength}
            disabled={isLoading}
          />
          {errors.listing_media && (
            <div className="pb-s">
              <CustomInputError errorKey={errors.listing_media[0]} />
            </div>
          )}
          {hasMedia ? (
            <MediaGrid
              className="block"
              key={activeMediaLength}
              mediaItems={filteredMedia}
              withActions
              onDelete={handleDelete}
              onCrop={handleCrop}
              onReorder={handleReorder}
              lastPosition={activeMediaLength}
              onImageClick={handleMediaClick}
              sameSize
              showHeroImageLabel
              disabled={isLoading}
            />
          ) : (
            <DragAndDrop
              className="block"
              onFileListChange={onFileListChange}
              maxCount={MAX_COUNT}
              maxCountErrorMessage={`${t("listings.tabs.media.max_photos_error")} ${MAX_COUNT}`}
              initialCount={activeMediaLength}
              disabled={isLoading}
            />
          )}
          <VideoPlayerModal open={videoModalOpen} handleClose={handleCloseVideoModal} selectedMedia={selectedMedia} />
          <MediaEditorModal
            visible={cropModalOpen}
            onClose={() => {
              setCropModalOpen(false)
              setMediaToCrop(null)
            }}
            media={mediaToCrop}
            onImageCropComplete={handleCropComplete}
            onVideoConfirm={handleVideoConfirm}
            targetWidth={1080}
            targetHeight={1080}
          />
        </div>
        <ListingActionButtons onFinish={onFinish} disabled={isLoading} />
      </div>
    </Spin>
  )
}
